import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, naira, Icon } from "../lib/core";

const SESSION_KEY = "topit_ai_session";
const getSession = () => {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) { s = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem(SESSION_KEY, s); }
  return s;
};

// ============================================================
// Savings Coach card (home)
// ============================================================
export const SavingsCoachCard = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/ai/savings-coach").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="ai-savings-coach"
      className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0B1220] to-[#14203a] border border-white/10 p-5">
      <div className="absolute -right-6 -top-6 w-28 h-28 bg-emerald-500/20 rounded-full blur-3xl" />
      <div className="relative flex items-start gap-3">
        <span className="shrink-0 w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center">
          <Icon name="auto_awesome" className="text-white text-2xl" />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-white font-extrabold font-display">AI Savings Coach</p>
            <span className="text-[9px] font-black bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded-full">AI</span>
          </div>
          <p className="text-slate-300 text-sm mt-1 leading-relaxed">{data.tip}</p>
          {data.potential_savings > 0 && (
            <p className="text-emerald-400 text-xs font-semibold mt-2">Potential: {naira(data.potential_savings)}/month in cashback</p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ============================================================
// Floating AI Assistant
// ============================================================
export const AIAssistant = ({ user, onPurchase, onOpenWallet }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [balance, setBalance] = useState(null);
  const [lastTx, setLastTx] = useState(null);
  const [session] = useState(getSession);
  const scrollRef = useRef(null);

  const scrollDown = () => setTimeout(() => scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }), 60);

  const refreshBalance = useCallback(async () => {
    try {
      const r = await api.get("/wallet");
      // Spendable balance for a vend = main balance + bonus (commission is NOT spendable)
      setBalance((r.data.balance || 0) + (r.data.bonus_balance || 0));
    } catch { /* noop */ }
  }, []);

  const loadLastTx = useCallback(async () => {
    try {
      const r = await api.get("/transactions/me");
      const list = r.data || [];
      // Only offer to repeat a genuinely completed top-up (never a failed one).
      const ok = list.find((t) => ["000", "OK", "PENDING"].includes(String(t.status_code)));
      setLastTx(ok || null);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    if (open) {
      refreshBalance();
      loadLastTx();
      if (messages.length === 0) {
        setMessages([{ role: "assistant", content: `Hi ${(user?.full_name || user?.name || "there").split(" ")[0]}! 👋 I'm your TopIt assistant. I can buy airtime or data, repeat your usual, or find the best value plan. What do you need?`, suggestions: ["Show me data plans", "Cheapest 2GB data", "Check my balance"] }]);
      }
    }
  }, [open]);

  const openWallet = () => { setOpen(false); onOpenWallet && onOpenWallet(); };

  // Close the assistant when a purchase succeeds so the branded receipt (rendered by the
  // dashboard) sits cleanly on top instead of behind the AI overlay.
  const handlePurchase = (recharge) => { setOpen(false); onPurchase && onPurchase(recharge); };

  // Append a ready-to-confirm action card client-side (from a plan chip or "buy again").
  const proposeAction = (action, intro) => {
    setMessages((m) => [...m, { role: "assistant", content: intro, action }]);
    scrollDown();
  };

  // Mark an action message as purchased so reopening the panel can't re-confirm it.
  const markConsumed = (idx) => setMessages((m) => m.map((msg, j) => (j === idx ? { ...msg, consumed: true } : msg)));

  const pickPlan = (plan) => proposeAction(
    { service: "data", provider: plan.provider, amount: plan.amount, subcode: plan.subcode, phone: null,
      label: `${plan.provider} ${(plan.label || "data").replace(/\s*[—-]\s*₦[\d,.]+/i, "").trim()} — ${naira(plan.amount)}` },
    "Great pick! Confirm below to buy this bundle:"
  );

  const buyAgain = () => {
    if (!lastTx) return;
    const svc = (lastTx.service_type || "airtime").toLowerCase();
    proposeAction(
      { service: svc, provider: lastTx.provider, amount: lastTx.amount, subcode: lastTx.subcode || null, phone: lastTx.customer_phone || null,
        label: `${lastTx.provider} ${naira(lastTx.amount)} ${svc}` },
      "Here's your last top-up — confirm to repeat it:"
    );
  };

  const lastTxLabel = lastTx ? `${lastTx.provider} ${naira(lastTx.amount)} ${(lastTx.service_type || "airtime").toLowerCase()}${lastTx.customer_phone ? ` · ${lastTx.customer_phone}` : ""}` : "";

  const send = useCallback(async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true); scrollDown();
    try {
      const r = await api.post("/ai/chat", { session_id: session, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply, action: r.data.action, plans: r.data.plans, suggestions: r.data.suggestions }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I'm having trouble right now. Please try again in a moment." }]);
    } finally { setBusy(false); scrollDown(); }
  }, [input, busy, session]);

  return (
    <>
      {/* FAB — button geometry stays fixed; only the inner icon bobs (stable hit target) */}
      <motion.button whileTap={{ scale: 0.9 }} onClick={() => setOpen(true)} data-testid="ai-fab"
        className="fixed z-[60] bottom-6 right-6 w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 shadow-xl shadow-fuchsia-600/40 flex items-center justify-center">
        <motion.span animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 2.5 }} className="flex items-center justify-center">
          <Icon name="auto_awesome" className="text-white text-2xl" />
        </motion.span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[65] bg-black/40 backdrop-blur-sm flex items-end sm:items-center sm:justify-end" onClick={() => setOpen(false)}>
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              onClick={(e) => e.stopPropagation()} data-testid="ai-panel"
              className="w-full sm:w-[420px] sm:mr-6 h-[85vh] sm:h-[600px] bg-[#0B1220] sm:rounded-3xl rounded-t-3xl border border-white/10 flex flex-col overflow-hidden">
              {/* header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center"><Icon name="auto_awesome" className="text-white" /></span>
                  <div>
                    <p className="text-white font-bold font-display leading-none">TopIt Assistant</p>
                    <p className="text-emerald-400 text-[10px] font-semibold mt-0.5">● Online</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {balance != null && (
                    <button onClick={openWallet} data-testid="ai-wallet-balance"
                      className="flex items-center gap-1 bg-white/10 hover:bg-white/15 border border-white/10 rounded-full px-3 py-1.5 transition-colors">
                      <Icon name="account_balance_wallet" className="text-emerald-400 text-sm" />
                      <span className="text-white text-xs font-bold">{naira(balance)}</span>
                    </button>
                  )}
                  <button onClick={() => setOpen(false)} data-testid="ai-close" className="text-slate-400 hover:text-white"><Icon name="close" /></button>
                </div>
              </div>

              {/* Buy again shortcut */}
              {lastTx && (
                <div className="px-4 pt-3">
                  <button onClick={buyAgain} data-testid="ai-buy-again"
                    className="w-full flex items-center gap-2 bg-gradient-to-r from-violet-600/20 to-fuchsia-600/20 hover:from-violet-600/30 hover:to-fuchsia-600/30 border border-fuchsia-500/30 rounded-xl px-3.5 py-2.5 transition-colors text-left">
                    <Icon name="replay" className="text-fuchsia-300 text-lg shrink-0" />
                    <span className="min-w-0">
                      <span className="block text-fuchsia-200 text-[10px] font-bold uppercase tracking-wide">Repeat last top-up</span>
                      <span className="block text-white text-sm font-semibold truncate">{lastTxLabel}</span>
                    </span>
                  </button>
                </div>
              )}

              {/* messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 no-scrollbar" data-testid="ai-messages">
                {messages.map((m, i) => (
                  <div key={i}>
                    <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${m.role === "user" ? "bg-red-600 text-white rounded-br-sm" : "bg-white/10 text-slate-100 rounded-bl-sm"}`}>
                        {m.content}
                      </div>
                    </div>
                    {m.action && (m.consumed ? (
                      <div className="mt-2 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-3 flex items-center gap-2" data-testid="ai-action-consumed">
                        <Icon name="check_circle" className="text-emerald-400" />
                        <p className="text-emerald-300 text-sm font-semibold">{m.action.label} — purchased ✓</p>
                      </div>
                    ) : (
                      <ActionCard action={m.action} user={user} balance={balance} onOpenWallet={openWallet}
                        onPurchase={(recharge) => { markConsumed(i); handlePurchase(recharge); }}
                        onDone={(txt) => send(txt)} onDebited={refreshBalance} />
                    ))}
                    {m.role === "assistant" && m.plans?.length > 0 && (
                      <div className="flex flex-col gap-2 mt-2" data-testid="ai-plan-chips">
                        {m.plans.map((p, j) => {
                          const allowance = (p.label || "data").replace(/\s*[—-]\s*₦[\d,.]+/i, "").trim() || "Data bundle";
                          return (
                            <button key={j} onClick={() => pickPlan(p)} data-testid={`ai-plan-${j}`}
                              className="flex items-center justify-between gap-2 text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-3.5 py-2.5 transition-colors group">
                              <span className="flex items-center gap-2 min-w-0">
                                <Icon name="wifi" className="text-fuchsia-400 text-base shrink-0" />
                                <span className="text-slate-100 text-sm font-semibold truncate">{p.provider} {allowance}</span>
                              </span>
                              <span className="text-emerald-400 text-sm font-bold shrink-0">{naira(p.amount)}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {m.role === "assistant" && m.suggestions?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {m.suggestions.map((s, j) => (
                          <button key={j} onClick={() => send(s)} data-testid={`ai-suggestion-${j}`}
                            className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 px-3 py-1.5 rounded-full transition-colors">{s}</button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {busy && (
                  <div className="flex justify-start"><div className="bg-white/10 px-4 py-3 rounded-2xl rounded-bl-sm">
                    <div className="flex gap-1">{[0, 1, 2].map((i) => <span key={i} className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />)}</div>
                  </div></div>
                )}
              </div>

              {/* input */}
              <div className="px-4 py-3 border-t border-white/10 flex items-center gap-2">
                <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  data-testid="ai-input" placeholder="Ask me anything…"
                  className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-fuchsia-500" />
                <button onClick={() => send()} disabled={busy} data-testid="ai-send"
                  className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center disabled:opacity-50">
                  <Icon name="send" className="text-white text-lg" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ---- action confirm card (buy) ----
const ActionCard = ({ action, user, balance, onOpenWallet, onPurchase, onDone, onDebited }) => {
  const [phone, setPhone] = useState(action.phone || user?.phone || "");
  const [state, setState] = useState("idle"); // idle | busy | done | error
  const [msg, setMsg] = useState("");

  const canAfford = balance == null || action.amount <= balance;

  const confirm = async () => {
    if (!phone || phone.replace(/\D/g, "").length < 10) { setMsg("Enter a valid phone number"); return; }
    if (!canAfford) { setMsg(`Your wallet balance (${naira(balance)}) isn't enough for this ${naira(action.amount)} purchase.`); return; }
    setState("busy"); setMsg("");
    try {
      const r = await api.post("/vend/wallet", {
        customer_phone: phone, amount: action.amount, provider: action.provider,
        service_type: action.service, subcode: action.subcode || null,
        use_wallet: true,
      });
      const ok = ["000", "OK", "PENDING"].includes(String(r.data.status_code));
      setState(ok ? "done" : "error");
      setMsg(ok ? "Purchase successful!" : (r.data.response_message || "Purchase could not be completed"));
      onDebited && onDebited();
      if (ok) {
        onPurchase && onPurchase({
          provider: action.provider,
          amount: action.amount,
          service_type: action.service,
          customer_phone: phone,
          processed_at: r.data.timestamp || new Date().toISOString(),
          delivered: String(r.data.status_code) !== "PENDING",
          celebrate: true,
        });
      }
    } catch (e) {
      setState("error");
      setMsg(e.response?.data?.detail || "Purchase failed. Check your wallet balance.");
    }
  };

  if (state === "done") {
    return (
      <div className="mt-2 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-3 flex items-center gap-2" data-testid="ai-action-done">
        <Icon name="check_circle" className="text-emerald-400" />
        <p className="text-emerald-300 text-sm font-semibold">{action.label} sent to {phone} 🎉</p>
      </div>
    );
  }
  return (
    <div className="mt-2 rounded-2xl bg-white/5 border border-white/10 p-3.5" data-testid="ai-action-card">
      <div className="flex items-center gap-2 mb-2">
        <Icon name={action.service === "data" ? "wifi" : "smartphone"} className="text-fuchsia-400" />
        <p className="text-white font-bold text-sm">{action.label || `${action.provider} ${action.service} ${naira(action.amount)}`}</p>
      </div>
      <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="ai-action-phone" placeholder="Recipient phone"
        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 outline-none focus:border-fuchsia-500 mb-2" />
      {balance != null && (
        <p className="text-[11px] text-slate-400 mb-2" data-testid="ai-action-balance">Wallet balance: <span className="font-semibold text-slate-200">{naira(balance)}</span></p>
      )}
      {!canAfford && (
        <div className="mb-2 rounded-xl bg-amber-500/10 border border-amber-500/30 p-2.5" data-testid="ai-action-insufficient">
          <p className="text-amber-300 text-xs leading-relaxed">
            Your balance ({naira(balance)}) isn't enough for this {naira(action.amount)} purchase. Fund your wallet to continue.
          </p>
          <button onClick={onOpenWallet} data-testid="ai-action-fund"
            className="mt-2 w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold py-2 rounded-lg">
            Fund wallet
          </button>
        </div>
      )}
      {msg && state === "error" && <p className="text-red-400 text-xs mb-2" data-testid="ai-action-error">{msg}</p>}
      <div className="flex gap-2">
        <button onClick={confirm} disabled={state === "busy" || !canAfford} data-testid="ai-action-confirm"
          className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-sm font-bold py-2.5 rounded-xl disabled:opacity-60">
          {state === "busy" ? "Processing…" : `Confirm · ${naira(action.amount)}`}
        </button>
        <button onClick={() => onDone && onDone("Actually, show me other options")} data-testid="ai-action-cancel"
          className="px-4 bg-white/5 text-slate-300 text-sm font-semibold rounded-xl">Change</button>
      </div>
    </div>
  );
};
